import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import apiRequestHeaders from '../../common/apiRequestHeaders';

export interface DateRange {
  from?: string;
  to?: string;
}

export interface SalesStatisticsFilter {
  weekNumbers: boolean;
  byCountry: boolean;
  byMonth: boolean;
  byService: boolean;
  parseCountryInfo: boolean;
  byAgent: boolean;
  byIH: boolean;
  byIHYllas: boolean;
  created?: DateRange;
  accommodationCreated?: DateRange;
  bookingStarts?: DateRange;
  bookingEnds?: DateRange;
  bookingNumber?: number;
  accommodationNumber?: number;
  customerNumber?: number;
  municipality?: string;
  bookingType?: number[];
  orderItemType?: string[];
  regions?: string[];
  skiResorts?: string[];
  agentNumber?: number;
  customerGroups?: string[];
  crossSiteIds?: string[];
}

export interface GetSalesStatisticsResponse {
  blobUrl: string;
  fileName: string;
  status: number;
}

export interface StartDateFilter {
  startDate?: DateRange;
}

export interface OwnerReportFilter {
  bookingsCreatedAtOrAfterDate: Date;
  bookingsStartAfterDate: Date;
  ownerId?: string;
  onlyNewCancelledBookings: boolean;
}

export const getSalesStatisticsAsync = createAsyncThunk(
  'statistics/getSalesStatistics',
  async (salesStatisticsFilter: SalesStatisticsFilter, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/statistics/statistics/getsalesstatistics',
        salesStatisticsFilter,
        {
          headers: {
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
          responseType: 'blob',
          timeout: 900000, // 15 minutes
        },
      );
      const { status } = response;
      let fileName = 'Kuukausitilasto.xlsx';
      if (salesStatisticsFilter.byAgent) {
        fileName = 'Edustajatilasto.xlsx';
      }
      if (salesStatisticsFilter.byService) {
        fileName = 'Palvelutilasto.xlsx';
      }
      if (salesStatisticsFilter.byCountry) {
        fileName = 'Maatilastotilasto.xlsx';
      }
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      return { blobUrl, fileName, status };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getTravelAgencyReportAsync = createAsyncThunk(
  'statistics/getTravelAgencyReport',
  async (filter: StartDateFilter, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/statistics/statistics/getTravelAgencyReport',
        filter,
        {
          headers: {
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
          responseType: 'blob',
          timeout: 900000, // 15 minutes
        },
      );
      const { status } = response;
      const fileName = 'MatkatoimistoRaportti.xlsx';
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      return { blobUrl, fileName, status };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getInterHomeReportAsync = createAsyncThunk(
  'statistics/getInterHomeReport',
  async (filter: StartDateFilter, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/statistics/statistics/getInterHomeReport',
        filter,
        {
          headers: {
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
          responseType: 'blob',
          timeout: 900000, // 15 minutes
        },
      );
      const { status } = response;
      const fileName = 'InterHome_Raportti.xlsx';
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      return { blobUrl, fileName, status };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getOwnersWhoWantPostBookingsReportReportAsync = createAsyncThunk(
  'statistics/getOwnersWhoWantPostBookingsReportReportAsync',
  async (filter: OwnerReportFilter, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/statistics/statistics/getOwnerReport',
        filter,
        {
          headers: {
            ...apiRequestHeaders.csrfHeader(),
            ...apiRequestHeaders.activeSiteHeader(),
          },
          responseType: 'blob',
          timeout: 900000, // 15 minutes
        },
      );
      const { status } = response;
      const fileName = 'Omistaja_Raportti.pdf';
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/pdf',
        }),
      );
      return { blobUrl, fileName, status };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);
